import Input from '../../../Utility/FormElements/Input';
import { StoreI18n, StoreSettings } from '../../../../Types/StoreTypes';
import { Button } from '../../../Utility/Button';
import { Badge, BadgeVariant } from '../../../Utility/Badge';

interface ExpertSettingsProps {
    SettingsActions: any;
    i18n: StoreI18n['i18n'];
    Currentsettings: StoreSettings;
}

function ExpertSettings({ SettingsActions, i18n, Currentsettings }: ExpertSettingsProps) {
    const handleVectorWeightChange = (value: string) => {
        const percentageValue = Math.max(1, Math.min(99, parseFloat(value) || 50));
        const vectorWeight = percentageValue / 100;
        const textWeight = 1.0 - vectorWeight;
        SettingsActions.updateCurrentSettings({
            hybridSearchVectorWeight: vectorWeight,
            hybridSearchTextWeight: textWeight,
        });
    };

    const handleTextWeightChange = (value: string) => {
        const percentageValue = Math.max(1, Math.min(99, parseFloat(value) || 50));
        const textWeight = percentageValue / 100;
        const vectorWeight = 1.0 - textWeight;
        SettingsActions.updateCurrentSettings({
            hybridSearchVectorWeight: vectorWeight,
            hybridSearchTextWeight: textWeight,
        });
    };

    const handleChunkSizeChange = (value: string) => {
        const chunkSize = Math.max(100, Math.min(10000, parseInt(value) || 2000));
        SettingsActions.updateCurrentSettings({ chunkSize });

        if (Currentsettings.chunkOverlap >= chunkSize) {
            SettingsActions.updateCurrentSettings({ chunkOverlap: Math.max(0, chunkSize - 1) });
        }
    };

    const handleChunkOverlapChange = (value: string) => {
        const chunkOverlap = Math.max(0, Math.min(Currentsettings.chunkSize - 1, parseInt(value) || 0));
        SettingsActions.updateCurrentSettings({ chunkOverlap });
    };

    return (
        <div className="w-full flex gap-8 flex-col">
            <h4 className="block">{i18n.translate({ id: 'expertSettings', sourceName: 'Settings' })}</h4>

            <div className="flex flex-col gap-4">
                <h5 className="text-lg font-semibold">
                    {i18n.translate({ id: 'hybridSearchWeights', sourceName: 'Settings' })}
                </h5>
                <p className="text-sm text-gray-500 mb-2">
                    {i18n.translate({ id: 'hybridSearchWeightDescription', sourceName: 'Settings' })}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label htmlFor="hybridSearchVectorWeight" className="flex flex-col gap-2">
                        {i18n.translate({ id: 'hybridSearchVectorWeight', sourceName: 'Settings' })}
                        <div className="flex items-center gap-2">
                            <Input
                                inputType="number"
                                value={((Currentsettings.hybridSearchVectorWeight ?? 0.5) * 100).toFixed(0)}
                                onChange={handleVectorWeightChange}
                                inputProps={{ min: 1, max: 99, step: 1 }}
                                id="hybridSearchVectorWeight"
                                className="flex-1"
                            />
                            <span className="text-sm">%</span>
                        </div>
                    </label>

                    <label htmlFor="hybridSearchTextWeight" className="flex flex-col gap-2">
                        {i18n.translate({ id: 'hybridSearchTextWeight', sourceName: 'Settings' })}
                        <div className="flex items-center gap-2">
                            <Input
                                inputType="number"
                                value={((Currentsettings.hybridSearchTextWeight ?? 0.5) * 100).toFixed(0)}
                                onChange={handleTextWeightChange}
                                inputProps={{ min: 1, max: 99, step: 1 }}
                                id="hybridSearchTextWeight"
                                className="flex-1"
                            />
                            <span className="text-sm">%</span>
                        </div>
                    </label>
                </div>

                <Badge variant={BadgeVariant.info} className="w-full">
                    <strong>{i18n.translate({ id: 'currentDistribution', sourceName: 'Settings' })}:</strong>{' '}
                    {Math.round((Currentsettings.hybridSearchVectorWeight ?? 0.5) * 100)}%{' '}
                    {i18n.translate({ id: 'vector', sourceName: 'Settings' })} /{' '}
                    {Math.round((Currentsettings.hybridSearchTextWeight ?? 0.5) * 100)}%{' '}
                    {i18n.translate({ id: 'fullText', sourceName: 'Settings' })}
                </Badge>
            </div>

            <div className="flex flex-col gap-4">
                <h5 className="text-lg font-semibold">
                    {i18n.translate({ id: 'chunkConfiguration', sourceName: 'Settings' })}
                </h5>
                <p className="text-sm text-gray-500 mb-2">
                    {i18n.translate({ id: 'chunkConfigurationDescription', sourceName: 'Settings' })}
                </p>

                <Badge variant={BadgeVariant.warning} className="w-full">
                    <strong>{i18n.translate({ id: 'chunkReembeddingWarning', sourceName: 'Settings' })}</strong>
                </Badge>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label htmlFor="chunkSize" className="flex flex-col gap-2">
                        {i18n.translate({ id: 'chunkSize', sourceName: 'Settings' })}
                        <div className="flex items-center gap-2">
                            <Input
                                inputType="number"
                                value={Currentsettings.chunkSize || 2000}
                                onChange={handleChunkSizeChange}
                                inputProps={{ min: 100, max: 10000, step: 100 }}
                                id="chunkSize"
                                className="flex-1"
                            />
                            <span className="text-sm">
                                {i18n.translate({ id: 'characters', sourceName: 'Settings' })}
                            </span>
                        </div>
                        <p className="text-xs text-gray-500">
                            {i18n.translate({ id: 'chunkSizeDescription', sourceName: 'Settings' })}
                        </p>
                    </label>

                    <label htmlFor="chunkOverlap" className="flex flex-col gap-2">
                        {i18n.translate({ id: 'chunkOverlap', sourceName: 'Settings' })}
                        <div className="flex items-center gap-2">
                            <Input
                                inputType="number"
                                value={Currentsettings.chunkOverlap || 500}
                                onChange={handleChunkOverlapChange}
                                inputProps={{ min: 0, max: (Currentsettings.chunkSize || 2000) - 1, step: 50 }}
                                id="chunkOverlap"
                                className="flex-1"
                            />
                            <span className="text-sm">
                                {i18n.translate({ id: 'characters', sourceName: 'Settings' })}
                            </span>
                        </div>
                        <p className="text-xs text-gray-500">
                            {i18n.translate({ id: 'chunkOverlapDescription', sourceName: 'Settings' })}
                        </p>
                    </label>
                </div>

                <Badge variant={BadgeVariant.info} className="w-full">
                    {i18n.translate({ id: 'chunkReembeddingInfo', sourceName: 'Settings' })}
                </Badge>
            </div>

            <div className="mt-5 text-right">
                <Button onClick={SettingsActions.handleUpdateSettings}>
                    {i18n.translate({ id: 'save', sourceName: 'Main' })}
                </Button>
            </div>
        </div>
    );
}

export default ExpertSettings;
